// UPSTACK_GAHNEM | Product Compare Web Component

class ProductCompare extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.closest('.shopify-section').id.replace('shopify-section-', '');
    this._backdrop = null;
    this.bindEvents();
  }

  connectedCallback() {
    const preloaded = parseInt(this.dataset.preloadedColumns || '1');
    this._setColumnCount(preloaded);

    // Listen for same-page trigger; only fills column 1 if it is actually empty
    this._onAutofill = (e) => {
      const col1 = this.querySelector('.feature-chart__product[data-column="1"]');
      const isEmpty = !col1?.querySelector('a.bold.line-clamp');
      if (isEmpty) {
        this.selectProduct(e.detail.handle, e.detail.column || '1');
      }
    };
    document.addEventListener('compare:autofill', this._onAutofill);

    // Cross-page: product handle stored by CompareTrigger on a different page
    const storedHandle = sessionStorage.getItem('compare_autofill');
    if (storedHandle) {
      sessionStorage.removeItem('compare_autofill');
      const col1 = this.querySelector('.feature-chart__product[data-column="1"]');
      if (!col1?.querySelector('a.bold.line-clamp')) {
        this.selectProduct(storedHandle, '1');
      }
    }
  }

  disconnectedCallback() {
    document.removeEventListener('compare:autofill', this._onAutofill);
    this._removeBackdrop();
  }

  // ─── Backdrop (lives on document.body to avoid stacking-context issues) ────

  _showBackdrop(drawerEl) {
    this._removeBackdrop();
    const bd = document.createElement('div');
    bd.className = 'product-compare-backdrop';
    bd.addEventListener('click', () => this._closeDrawer(drawerEl));
    document.body.appendChild(bd);
    this._backdrop = bd;
  }

  _removeBackdrop() {
    this._backdrop?.remove();
    this._backdrop = null;
  }

  _openDrawer(drawerId) {
    const drawer = this.querySelector(`#${drawerId}`);
    if (!drawer) return;
    drawer.removeAttribute('hidden');
    this._showBackdrop(drawer);
    drawer.querySelector('input[data-action="search"]')?.focus();
  }

  _closeDrawer(drawerEl) {
    drawerEl?.setAttribute('hidden', '');
    this._removeBackdrop();
  }

  // ─── Column visibility ────────────────────────────────────────────────────

  _setColumnCount(count) {
    const section = this.closest('.shopify-section');
    section.style.setProperty('--feature-chart-values-columns-count', count);

    const stackMain = section.querySelector('.section-stack__main');
    if (!stackMain) return;

    if (this.dataset.textPosition === 'center') {
      const widths = { 1: '680px', 2: '900px', 3: '100%' };
      stackMain.style.width = widths[count] || '100%';
    } else {
      const flexBases = { 1: '600px', 2: '900px', 3: 'auto' };
      stackMain.style.flexBasis = flexBases[count] || 'auto';
      stackMain.style.minWidth  = count === 3 ? '0' : '450px';
    }
  }

  _unlockColumn(column) {
    this.querySelectorAll(`[data-column="${column}"][data-column-locked]`).forEach(el => {
      el.removeAttribute('data-column-locked');
    });
  }

  _revealNextSlot() {
    const nextLocked = this.querySelector('[data-column-locked]');
    if (!nextLocked) return;

    this._unlockColumn(nextLocked.dataset.column);

    const visibleCount = [1, 2, 3].filter(n =>
      this.querySelector(`[data-column="${n}"]:not([data-column-locked])`) !== null
    ).length;

    this._setColumnCount(visibleCount);
  }

  // ─── Events ───────────────────────────────────────────────────────────────

  bindEvents() {
    this.querySelectorAll('[aria-controls^="product-search-drawer-"]').forEach(btn => {
      btn.addEventListener('click', () => this._openDrawer(btn.getAttribute('aria-controls')));
    });

    this.querySelectorAll('[data-action="close"]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const drawer = e.currentTarget.closest('.product-compare-search');
        this._closeDrawer(drawer);
      });
    });

    this.querySelectorAll('input[data-action="search"]').forEach(input => {
      let timeout;
      input.addEventListener('input', (e) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => this.performSearch(e.target), 300);
      });
    });
  }

  // ─── Search ───────────────────────────────────────────────────────────────

  async performSearch(input) {
    const query = input.value.trim();
    const drawer = input.closest('.product-compare-search');
    const resultsContainer = drawer.querySelector('.product-compare-search__results');
    const column = input.closest('.feature-chart__product')?.dataset.column
      || drawer.dataset.column;

    if (!query) {
      resultsContainer.innerHTML = '';
      return;
    }

    try {
      const res = await fetch(
        `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`
      );
      const data = await res.json();
      const products = data.resources.results.products;

      if (products.length === 0) {
        resultsContainer.innerHTML = '<p class="text-subdued text-center" style="padding:1.25rem 1.25rem">No products found.</p>';
        return;
      }

      resultsContainer.innerHTML = products.map(p => `
        <div class="product-compare-result" data-handle="${p.handle}" data-column="${column}">
          <img src="${p.image || ''}" alt="${p.title}" width="44" height="44">
          <div class="v-stack sm:gap-0.5">
            <span class="bold line-clamp">${p.title}</span>
            <span class="text-subdued">${p.price}</span>
          </div>
        </div>
      `).join('');

      resultsContainer.querySelectorAll('.product-compare-result').forEach(el => {
        el.addEventListener('click', () => {
          this._closeDrawer(drawer);
          this.selectProduct(el.dataset.handle, el.dataset.column);
        });
      });
    } catch (e) {
      console.error('[UPSTACK_GAHNEM] Compare search error', e);
    }
  }

  // ─── Select & update ──────────────────────────────────────────────────────

  async selectProduct(handle, column) {
    this._unlockColumn(column);
    const currentVisible = [1, 2, 3].filter(n =>
      this.querySelector(`[data-column="${n}"]:not([data-column-locked])`) !== null
    ).length;
    this._setColumnCount(currentVisible);

    this.querySelectorAll(`.feature-chart__product[data-column="${column}"]`).forEach(s => {
      s.style.opacity = '0.5';
    });

    try {
      const res  = await fetch(`${window.Shopify.routes.root}products/${handle}?section_id=${this.sectionId}`);
      const text = await res.text();
      const doc  = new DOMParser().parseFromString(text, 'text/html');
      const tpl  = doc.querySelector('#product-compare-api');
      if (!tpl) throw new Error('product-compare-api template not found in Section Rendering response');

      this.updateColumn(column, JSON.parse(tpl.innerHTML));
      this._revealNextSlot();
    } catch (e) {
      console.error('[UPSTACK_GAHNEM] selectProduct error', e);
    } finally {
      this.querySelectorAll(`.feature-chart__product[data-column="${column}"]`).forEach(s => {
        s.style.opacity = '1';
      });
    }
  }

  updateColumn(column, data) {
    const imageHtml = data.image
      ? `<a href="${data.url}"><img src="${data.image}" alt="${data.title}" sizes="(max-width:699px) 80px,150px" width="150" height="150"></a>`
      : '';

    const slotHTML = `
      <div class="v-stack gap-5">
        ${imageHtml}
        <div class="v-stack gap-2">
          <div class="v-stack sm:gap-0.5">
            <a href="${data.url}" class="bold line-clamp">${data.title}</a>
            <div class="price-list"><span class="price">${data.price_html}</span></div>
          </div>
        </div>
        <button type="button" class="button button--subdued w-full"
                data-compare-align-button
                aria-controls="product-search-drawer-${column}" aria-expanded="false">
          Change Product
        </button>
      </div>
      <div class="product-compare-search"
           id="product-search-drawer-${column}"
           data-column="${column}"
           hidden>
        <div class="product-compare-search__header">
          <input type="search" class="input"
                 placeholder="Search for a product…"
                 data-action="search"
                 autocomplete="off" />
          <button type="button" data-action="close"
                  class="text-subdued hover:text-primary"
                  aria-label="Close search"
                  style="flex-shrink:0;">
            <svg role="presentation" focusable="false" width="14" height="14" viewBox="0 0 12 12">
              <path fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"
                d="M7.414 6l4.293-4.293A.999.999 0 1 0 10.293.293L6 4.586
                   1.707.293A.999.999 0 1 0 .293 1.707L4.586 6 .293 10.293
                   a.999.999 0 1 0 1.414 1.414L6 7.414l4.293 4.293
                   a.997.997 0 0 0 1.414 0 .999.999 0 0 0 0-1.414L7.414 6z"/>
            </svg>
          </button>
        </div>
        <div class="product-compare-search__results"></div>
      </div>
    `;

    this.querySelectorAll(`.feature-chart__product[data-column="${column}"]`).forEach(slot => {
      slot.innerHTML = slotHTML;
    });

    this.bindEvents();

    this.querySelectorAll(`.feature-chart__value[data-column="${column}"]`).forEach(cell => {
      const key = cell.dataset.metafield;
      const val = (!key || key === '') ? data.price_html : data.metafields[key];
      cell.innerHTML = this.formatValue(val);
    });
  }

  formatValue(val) {
    if (val === null || val === undefined || val === '') return '—';
    if (val === 'TRUE'  || val === true)  return '<svg fill="none" class="icon offset-icon" width="20" height="16" viewBox="0 0 20 16" style="--icon-height:16px"><path d="m2 7.6 5.259 5.6L18 2" stroke="currentColor" stroke-width="3"/></svg>';
    if (val === 'FALSE' || val === false) return '<svg fill="none" class="icon offset-icon" width="16" height="16" viewBox="0 0 16 16" style="--icon-height:16px"><path d="m.94 3.06 12 12 2.12-2.12-12-12L.94 3.06Zm2.12 12 12-12L12.94.94l-12 12 2.12 2.12Z" fill="currentColor"/></svg>';
    return val;
  }
}

if (!customElements.get('product-compare')) {
  customElements.define('product-compare', ProductCompare);
}
